import React, { useState } from "react";
import {
  Globe2,
  Film,
  Clock,
  RefreshCw,
  MonitorPlay,
  AudioLines,
  Network,
  Activity,
  Radio,
  ChevronRight,
  Code2,
} from "lucide-react";
import { useI18n } from "../../i18n";
import { Container, Section, SectionHead, Card, Badge } from "../../components/ui/Primitives";
import { PageHero, CTASection } from "../../components/marketing/Shared";
import { cn } from "../../lib/utils";

const CORE = [
  { icon: Globe2, title: "Browser capture, cross-OS, no install", tech: "WebSocket/WebCodecs transport — encoded frames, connection id + resume token, keyframe on-resume, timestamp sync (Zoom-fidelity model).", desc: "Works on any OS and browser with nothing to install." },
  { icon: Film, title: "Continuous, no-re-encode recording", tech: "Each source records unconditionally start-to-end (gap-free files); overlay/encode is deferred to a separate service (defer-encode) to scale ingestion.", desc: "Every source is captured whole, without re-encoding on the hot path." },
  { icon: Clock, title: "Server-only timestamp", tech: "Evidence precision comes from cutting latency server-side, never trusting the client clock.", desc: "Tamper-proof time on every frame." },
  { icon: RefreshCw, title: "Autonomous capture client", tech: "Detects and self-recovers from half-open connections, ended tracks and freezes.", desc: "Reconnects on its own — no proctor intervention." },
  { icon: MonitorPlay, title: "Up to 1080p pipeline", tech: "Optimized publish (~2.5 Mbps) across the whole pipeline.", desc: "Full-HD fidelity at efficient bitrates." },
  { icon: AudioLines, title: "Configurable audio", tech: "AAC/Opus codec, sample-rate/bitrate by config, mono; audio can be disabled per profile.", desc: "Tune or disable audio per exam profile." },
  { icon: Network, title: "Horizontal sharding", tech: "Live relay and recording distributed by session UUID.", desc: "Scale out linearly across shards." },
  { icon: Activity, title: "Per-session telemetry & health", tech: "Ingest throughput, negotiated codec, traffic stats, stalled-source detection with client-vs-server root cause.", desc: "Know the health of every session in real time." },
  { icon: Radio, title: "Professional stream outputs", tech: "NDI, SRT, RTSP, OBS/MediaMTX integration.", desc: "Broadcast-grade outputs for live production." },
];

const PIPELINE = [
  { key: "client", accent: "brand" },
  { key: "ingest", accent: "brand" },
  { key: "recording", accent: "brand" },
  { key: "renderer", accent: "exams" },
  { key: "review", accent: "exams" },
  { key: "relay", accent: "ctf" },
  { key: "output", accent: "ctf" },
];

export default function Features() {
  const { t } = useI18n();
  const [tech, setTech] = useState(false);

  return (
    <>
      <PageHero
        breadcrumb={t("nav.features")}
        eyebrow={t("features.hero.eyebrow")}
        title={t("features.hero.title")}
        lead={t("features.hero.lead")}
      >
        <button
          onClick={() => setTech((v) => !v)}
          className={cn(
            "inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors",
            tech ? "border-brand bg-brand/15 text-brand" : "border-white/25 text-white hover:bg-white/10"
          )}
        >
          <Code2 className="h-4 w-4" />
          {t("features.forEngineers")}
        </button>
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {CORE.map((f) => (
              <Card key={f.title} className="p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/12 text-brand">
                  <f.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-base font-bold">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tech ? f.tech : f.desc}</p>
                {tech && (
                  <Badge tone="brand" className="mt-3">
                    <Code2 className="h-3 w-3" /> technical
                  </Badge>
                )}
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      {/* Architecture diagram */}
      <Section alt>
        <Container>
          <SectionHead center title={t("features.arch.title")} />
          <div className="mt-12 overflow-x-auto">
            <div className="flex min-w-max items-center justify-center gap-2 pb-2">
              {PIPELINE.map((p, i) => (
                <React.Fragment key={p.key}>
                  <div
                    className="flex h-24 w-40 flex-col items-center justify-center rounded-xl border bg-card p-3 text-center"
                    style={{ borderColor: `hsl(var(--${p.accent}) / 0.4)` }}
                  >
                    <span
                      className="mb-1 font-mono text-[10px] font-bold uppercase tracking-wider"
                      style={{ color: `hsl(var(--${p.accent}))` }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm font-semibold leading-tight">{t(`features.arch.${p.key}`)}</span>
                  </div>
                  {i < PIPELINE.length - 1 && <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />}
                </React.Fragment>
              ))}
            </div>
          </div>
          <p className="mt-6 text-center font-mono text-xs text-muted-foreground">
            Client (browser) → Ingest (WebSocket/ASGI) → Recording (raw buckets) → Renderer (overlay + encode) → Review/DB + Live relay → Event Wall / OBS
          </p>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}
